import React, { useRef, useState, useEffect } from "react";
import TalkToFozzilButton from "./TalkToFozzilButton";
import FadeInOnScroll from "./FadeInOnScroll";
import UpgradeFozzilButton from "./UpgradeFozzilButton";

const PROMPT_SUGGESTIONS = [
  "Suggest ways to grow my business",
  "Create a healthy weekly meal plan",
  "எனக்கு ஒரு சிறந்த வாழ்க்கை திட்டம் சொல்",
  "எனது குழுவுக்கு பிரேரணை வார்த்தைகள் தரு",
  "ஒரு புதிய பிஸினஸ் ஐடியா பரிந்துரை செய்",
  "मेरे व्यवसाय को बढ़ाने के तरीके बताओ",
  "मुझे रोज़ की उत्पादकता बढ़ाने के सुझाव दो",
  "నాకు ఉత్తమ జీవన పథకాన్ని సూచించు",
  "ನನ್ನ ತಂಡಕ್ಕೆ ಪ್ರೇರಣಾದಾಯಕ ಮಾತುಗಳು ಹೇಳು",
  "എനിക്ക് ഒരു പുതിയ ബിസിനസ് ആശയം പറയൂ",
];

const images = [
  process.env.PUBLIC_URL + "/wallpaper1.png",
  process.env.PUBLIC_URL + "/wallpaper2.png",
  process.env.PUBLIC_URL + "/wallpaper3.png",
  process.env.PUBLIC_URL + "/wallpaper4.png",
  process.env.PUBLIC_URL + "/wallpaper5.png",
  process.env.PUBLIC_URL + "/wallpaper6.png",
];

const ImageCarousel = () => {
  const [current, setCurrent] = useState(0);
  const total = images.length;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev === total - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [total]);

  return (
    <div className="relative flex items-center justify-center my-8 overflow-hidden">
      <div className="w-full overflow-hidden rounded-2xl">
        <div
          className="flex w-full transition-transform duration-800 ease-in-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {images.map((src, idx) => (
            <img
              key={idx}
              src={src}
              alt={`Wallpaper ${idx + 1}`}
              className="w-full max-h-[200px] sm:max-h-[600px] rounded-2xl flex-shrink-0"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const MainContent = ({ setShowFozzilPill }) => {
  const heroRef = useRef(null);
  const restOfPageRef = useRef(null);
  const [searchValue, setSearchValue] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const [promptIndex, setPromptIndex] = useState(0);
  const [typedText, setTypedText] = useState("");
  const inputRef = useRef(null);
  const typingTimerRef = useRef(null);
  const advanceTimerRef = useRef(null);
  const charIndexRef = useRef(0);

  useEffect(() => {
    if (!setShowFozzilPill) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setShowFozzilPill(!entry.isIntersecting),
      { threshold: 0.05 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, [setShowFozzilPill]);

  // Typing animation for prompt suggestions (stable, cancellable)
  useEffect(() => {
    // Clear any existing timers first
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }

    if (inputFocused || searchValue) {
      setTypedText("");
      return;
    }

    const prompt = PROMPT_SUGGESTIONS[promptIndex] || "";
    charIndexRef.current = 0;
    setTypedText("");

    // Use setInterval and refs so we can reliably clear timers on cleanup
    typingTimerRef.current = setInterval(() => {
      charIndexRef.current += 1;
      if (charIndexRef.current <= prompt.length) {
        setTypedText(prompt.slice(0, charIndexRef.current));
      } else {
        // finished typing this prompt
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
        // pause before advancing to next prompt
        advanceTimerRef.current = setTimeout(() => {
          setPromptIndex((prev) => (prev + 1) % PROMPT_SUGGESTIONS.length);
        }, 1200);
      }
    }, 40);

    return () => {
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
      }
      if (advanceTimerRef.current) {
        clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    };
  }, [promptIndex, inputFocused, searchValue]);

  const handleSearch = (e) => {
    e.preventDefault();
    window.open(
      "https://intelligence.fozzil.in/",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handlePromptClick = (prompt) => {
    setSearchValue(prompt);
    setInputFocused(true);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  const ExpertiseContent3 = () => (
    <div className="w-screen bg-black flex justify-center items-center p-0 m-0">
      <div className="flex flex-row justify-center items-start gap-[2vw] w-full max-w-[1400px] mx-auto py-8 pb-6">
        <div className="flex-1 max-w-[600px] flex flex-col items-start mx-[0.5vw]">
          <img
            src={process.env.PUBLIC_URL + "/nlp.png"}
            alt="Natural Language Processing"
            className="w-full max-w-[480px] max-h-[320px] object-cover rounded-md mb-6 bg-gray-800 block mx-auto"
          />
          <h2 className="text-[#0099f2] text-2xl font-secondary font-normal text-justify mb-3 mt-0 leading-tight">
            Natural Language Processing
          </h2>
          <p className="text-white text-base font-secondary text-justify mt-0 mb-6 leading-relaxed max-w-[95%]">
            We leverage the latest advancements in natural language processing
            to create intelligent chatbots, voice assistants, and sentiment
            analysis
          </p>
        </div>
        <div className="flex-1 max-w-[600px] flex flex-col items-start mx-[0.5vw]">
          <img
            src={process.env.PUBLIC_URL + "/scalability.png"}
            alt="Scalability and Flexibility"
            className="w-full max-w-[480px] max-h-[320px] object-cover rounded-md mb-6 bg-gray-800 block mx-auto"
          />
          <h2 className="text-[#0099f2] text-2xl font-secondary font-normal text-justify mb-3 mt-0 leading-tight">
            Scalability and Flexibility
          </h2>
          <p className="text-white text-base font-secondary text-justify mt-0 mb-6 leading-relaxed max-w-[95%]">
            Our solutions are designed to scale seamlessly as your business
            grows. We adapt to your evolving needs and provide the flexibility
            to integrate our AI systems with your existing infrastructure.
          </p>
        </div>
      </div>
    </div>
  );

  const ExpertiseGrid = () => (
    <div className="w-screen bg-black flex justify-center items-center p-0 m-0">
      <div className="flex flex-row justify-center items-start gap-[2vw] w-full max-w-[1400px] mx-auto py-8 pb-6">
        <div className="flex-1 max-w-[600px] flex flex-col items-start mx-[0.5vw]">
          <img
            src={process.env.PUBLIC_URL + "/vision.png"}
            alt="Computer Vision"
            className="w-full max-w-[480px] max-h-[320px] object-cover rounded-md mb-6 bg-gray-800 block mx-auto"
          />
          <h2 className="text-[#0099f2] text-2xl font-secondary font-normal text-justify mb-3 mt-0 leading-tight">
            Computer Vision
          </h2>
          <p className="text-white text-base font-secondary text-justify mt-0 mb-6 leading-relaxed max-w-[95%]">
            Our cutting-edge computer vision solutions enable you to extract
            valuable insights from visual data, revolutionizing industries such
            as healthcare, retail, manufacturing, and more.
          </p>
        </div>
        <div className="flex-1 max-w-[600px] flex flex-col items-start mx-[0.5vw]">
          <img
            src={process.env.PUBLIC_URL + "/solution.png"}
            alt="Tailored Solutions"
            className="w-full max-w-[480px] max-h-[320px] object-cover rounded-md mb-6 bg-gray-800 block mx-auto"
          />
          <h2 className="text-[#0099f2] text-2xl font-secondary font-normal text-justify mb-3 mt-0 leading-tight">
            Tailored Solutions
          </h2>
          <p className="text-white text-base font-secondary text-justify mt-0 mb-6 leading-relaxed max-w-[95%]">
            We believe in the power of customization. We work closely with you
            to understand your unique business requirements and deliver
            tailor-made AI solutions that align with your goals.
          </p>
        </div>
      </div>
    </div>
  );

  const ExpertiseShowcase = () => (
    <div className="w-full bg-black flex flex-col items-center justify-center p-0 m-0 overflow-x-hidden">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 py-8 md:py-12">
        {/* Computer Vision */}
        <div className="bg-transparent rounded-2xl shadow-xl p-6 md:p-8 flex flex-col items-center justify-start w-full">
          <img
            src={process.env.PUBLIC_URL + "/vision.png"}
            alt="Computer Vision"
            className="w-full max-w-[320px] max-h-[200px] object-cover rounded-md mb-6 bg-gray-800"
          />
          <h2 className="text-[#0099f2] text-xl font-secondary font-normal text-center mb-3 mt-0 leading-tight">
            Adaptive Intelligence
          </h2>
          <p className="text-white text-base font-secondary text-center mt-0 mb-2 leading-relaxed w-full">
            Not just seeing — understanding, evolving, anticipating.
            <br />
            Our computer vision doesn’t just detect. It learns from every frame,
            every context, every shift in behavior. From spotting early health
            risks to predicting manufacturing flaws before they happen, our
            systems grow smarter with you. This isn’t AI — it’s adaptive
            awareness.
          </p>
        </div>
        {/* Tailored Solutions */}
        <div className="bg-transparent rounded-2xl shadow-xl p-6 md:p-8 flex flex-col items-center justify-start w-full">
          <img
            src={process.env.PUBLIC_URL + "/solution.png"}
            alt="Tailored Solutions"
            className="w-full max-w-[320px] max-h-[200px] object-cover rounded-md mb-6 bg-gray-800"
          />
          <h2 className="text-[#0099f2] text-xl font-secondary font-normal text-center mb-3 mt-0 leading-tight">
            Intelligent Evolution
          </h2>
          <p className="text-white text-base font-secondary text-center mt-0 mb-2 leading-relaxed w-full">
            Your business changes. Your goals shift. So should your AI.
            <br />
            We don’t build static tools — we build living partners. Our systems
            learn from your actions, feedback, and even your hesitations. The
            more you use us, the more we become you.
          </p>
        </div>
        {/* NLP */}
        <div className="bg-transparent rounded-2xl shadow-xl p-8 flex flex-col items-center justify-start">
          <img
            src={process.env.PUBLIC_URL + "/nlp.png"}
            alt="Natural Language Processing"
            className="w-full max-w-[320px] max-h-[200px] object-cover rounded-md mb-6 bg-gray-800"
          />
          <h2 className="text-[#0099f2] text-xl font-secondary font-normal text-center mb-3 mt-0 leading-tight">
            Human-Like Dialogue
          </h2>
          <p className="text-white text-base font-secondary text-center mt-0 mb-2 leading-relaxed w-full">
            Talk to us like you talk to a friend — because we are becoming one.
            <br />
            Our NLP isn’t just smart — it’s empathetic. It picks up on tone,
            remembers your preferences, adapts to your mood, and even learns
            your quirks. It doesn’t just answer — it connects. This is AI with
            heart.
          </p>
        </div>
        {/* Scalability & Flexibility */}
        <div className="bg-transparent rounded-2xl shadow-xl p-8 flex flex-col items-center justify-start">
          <img
            src={process.env.PUBLIC_URL + "/scalability.png"}
            alt="Scalability and Flexibility"
            className="w-full max-w-[320px] max-h-[200px] object-cover rounded-md mb-6 bg-gray-800"
          />
          <h2 className="text-[#0099f2] text-xl font-secondary font-normal text-center mb-3 mt-0 leading-tight">
            Self-Improving Systems
          </h2>
          <p className="text-white text-base font-secondary text-center mt-0 mb-2 leading-relaxed w-full">
            Scale isn’t just about size — it’s about smarts.
            <br />
            Our architecture is powered by AGI principles: it doesn’t just
            handle growth — it thinks its way through it. Automatically
            optimizes, self-corrects, and evolves performance in real time.
          </p>
        </div>
        {/* Smart Home */}
        <div className="bg-transparent border-2 border-blue-900 rounded-2xl shadow-xl p-6 md:p-8 flex flex-col items-center justify-start w-full md:col-span-2">
          <img
            src={process.env.PUBLIC_URL + "/wallpaper5.png"}
            alt="Smart Home"
            className="max-w-full max-h-[320px] w-auto h-auto mb-6"
          />
          <h2 className="text-[#0099f2] text-xl font-secondary font-normal text-center mb-3 mt-0 leading-tight">
            The Living Home
          </h2>
          <p className="text-white text-base font-secondary text-center mt-0 mb-2 leading-relaxed w-full">
            Your home isn’t just automated — it’s alive.
            <br />
            FOZZIL learns your rhythm: when you wake, how you like your lights,
            what music calms you, when you’re stressed. It turns on the coffee
            maker before you ask. It dims the lights when you’re sad. It’s not
            just connected — it’s intuitively you. Welcome to home, as your
            mind’s mirror.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <main className="flex-1">
        <section
          className="bg-gradient-to-r from-black to-[#232b6c] text-white min-h-[600px] flex flex-col items-center justify-center text-center rounded-[18px] mx-4"
          ref={heroRef}
        >
          <div className="max-w-[1000px] w-full px-12 flex flex-col items-center justify-center">
            <form
              className="rounded-full border border-transparent p-1.5 px-3 sm:p-2 sm:px-6 flex items-center justify-center w-full max-w-[700px] h-[54px] sm:h-[70px] mx-auto mb-10 relative"
              onSubmit={handleSearch}
              autoComplete="off"
              style={{
                background:
                  "linear-gradient(90deg, #0a0b1c 0%, #19204f 100%) padding-box, linear-gradient(90deg, #00d2ff 0%, #1a6dff 45%, #c822ff 100%) border-box",
                border: "1px solid transparent",
                boxShadow:
                  "0 6px 20px rgba(26,109,255,0.12), 0 0 18px rgba(200,34,255,0.08), 0 0 22px rgba(0,210,255,0.05), inset 0 0 0 2px #162a42, inset 0 0 8px rgba(26,109,255,0.04)",
              }}
            >
              <input
                type="text"
                className="flex-1 bg-transparent border-none outline-none text-white text-lg sm:text-2xl py-1.5 px-2 sm:py-2 sm:px-4 font-secondary rounded-full h-9 sm:h-10"
                placeholder={
                  inputFocused || searchValue ? "Command FOZZIL..." : ""
                }
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                ref={inputRef}
                style={{ position: "relative", background: "transparent" }}
              />
              {/* Typing animation prompt overlay */}
              {!inputFocused && !searchValue && (
                <span className="absolute left-2 sm:left-8 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs sm:text-base font-secondary pointer-events-none whitespace-pre-line transition-none z-2 opacity-100 truncate max-w-full sm:max-w-[90vw]">
                  {typedText}
                </span>
              )}
              <button
                type="submit"
                className="bg-none border-none outline-none cursor-pointer flex items-center rounded-full h-9 w-10 sm:h-10 sm:w-12 justify-center ml-0 sm:ml-2"
              >
                <svg
                  className="w-7 h-7 sm:w-9 sm:h-9 brightness-125 drop-shadow-[0_0_2px_#a259f7]"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 21L23 12L2 3V10L17 12L2 14V21Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </form>
            <h1 className="font-primary text-[2rem] mt-10 mb-5 tracking-wider uppercase text-[#0099f2]  ">
              INTELLIGENCE AT YOUR COMMAND!
            </h1>
            <h2 className="font-secondary text-2xl font-bold text-white mb-2">
              Intelligence that learns like you do.
            </h2>
          </div>
        </section>

        {/* Upgrade FOZZIL Button - After the container */}
        <div className="w-full bg-black py-8">
          <UpgradeFozzilButton />
        </div>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div ref={restOfPageRef}>
            <FadeInOnScroll>
              <ImageCarousel />
            </FadeInOnScroll>
            <FadeInOnScroll>
              <section className="mt-12 w-full text-white">
                <h2 className="font-primary text-5xl text-[#0099f2] text-left mb-2 font-variant-small-caps font-normal">
                  Our Expertise
                </h2>
                <hr className="border-none border-t-[1.5px] border-white mb-6 w-1/2" />
              </section>
            </FadeInOnScroll>
            <FadeInOnScroll>
              <ExpertiseShowcase />
            </FadeInOnScroll>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MainContent;
